// ============================================================================
// AUTHCONTEXT.JS - VERSIÓN COMPLETA CON INTERCEPTOR INTELIGENTE Y RELOAD
// ============================================================================
// Esta versión centraliza toda la autenticación y sincroniza automáticamente
// con TaskStore eliminando la duplicación de estado.
// NUEVO: Usa reload en lugar de redirect porque PrivateRoute maneja el login
// ============================================================================

import React, { createContext, useContext, useReducer, useEffect } from 'react';
import appConfig from '../config/appConfig';
import apiClient, { registerUnauthorizedHandler } from '../services/apiClient';

// La URL se obtiene dinámicamente desde la configuración
const getApiBaseUrl = () => appConfig.getApiUrl();

// Estados iniciales del contexto
const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  sessionExpired: false
};

// Tipos de acciones para el reducer
const AuthActions = {
  LOADING: 'LOADING',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  USER_UPDATE: 'USER_UPDATE',
  CLEAR_ERROR: 'CLEAR_ERROR',
  STOP_LOADING: 'STOP_LOADING'
};

// ============================================================================
// SISTEMA DE SINCRONIZACIÓN CON TASKSTORE
// ============================================================================
let taskStoreNotifier = null;

export const registerTaskStoreNotifier = (notifier) => {
  console.log('🔗 TaskStore registrado para notificaciones');
  taskStoreNotifier = notifier;
};

const notifyTaskStore = (type, data) => {
  if (taskStoreNotifier) {
    console.log(`📢 Notificando a TaskStore: ${type}`, data);
    try {
      taskStoreNotifier(type, data);
    } catch (error) {
      console.error('Error notificando a TaskStore:', error);
    }
  } else {
    console.log('⚠️ TaskStore no registrado, saltando notificación');
  }
};

// ============================================================================
// REDUCER CON NOTIFICACIONES AUTOMÁTICAS
// ============================================================================
function authReducer(state, action) {
  switch (action.type) {
    case AuthActions.LOADING:
      return {
        ...state,
        isLoading: true,
        error: null
      };

    case AuthActions.LOGIN_SUCCESS:
      console.log('✅ AuthReducer: LOGIN_SUCCESS', {
        user: action.payload.user?.nombre,
        hasToken: !!action.payload.token
      });
      
      notifyTaskStore('LOGIN_SUCCESS', {
        user: action.payload.user,
        token: action.payload.token
      });

      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        sessionExpired: false
      };

    case AuthActions.LOGIN_FAILURE:
      console.log('❌ AuthReducer: LOGIN_FAILURE', action.payload.error);
      
      notifyTaskStore('LOGIN_FAILURE', { 
        error: action.payload.error 
      });

      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload.error,
        sessionExpired: false
      };

    case AuthActions.LOGOUT:
      console.log('🚪 AuthReducer: LOGOUT - Limpiando estado');
      
      notifyTaskStore('LOGOUT', {});

      return {
        ...initialState,
        isLoading: false,
        sessionExpired: false
      };

    case AuthActions.SESSION_EXPIRED:
      console.log('🔒 AuthReducer: SESSION_EXPIRED - Limpiando estado');
      
      notifyTaskStore('SESSION_EXPIRED', {});

      return {
        ...initialState,
        isLoading: false,
        sessionExpired: true,
        error: 'Tu sesión ha expirado'
      };

    case AuthActions.STOP_LOADING:
      return {
        ...state,
        isLoading: false
      };

    case AuthActions.USER_UPDATE:
      notifyTaskStore('USER_UPDATE', { 
        user: action.payload.user 
      });
      
      return {
        ...state,
        user: { ...state.user, ...action.payload.user }
      };

    case AuthActions.CLEAR_ERROR:
      return {
        ...state,
        error: null,
        sessionExpired: false
      };

    default:
      return state;
  }
}

// ============================================================================
// UTILIDADES PARA LOCALSTORAGE
// ============================================================================
const TokenStorage = {
  getToken: () => {
    try {
      return localStorage.getItem('token');
    } catch (error) {
      console.error('Error obteniendo token:', error);
      return null;
    }
  },

  setToken: (token) => {
    try {
      if (token) {
        localStorage.setItem('token', token);
        console.log('💾 Token guardado en localStorage');
      }
    } catch (error) {
      console.error('Error guardando token:', error);
    }
  },

  removeToken: () => {
    try {
      localStorage.removeItem('token');
    } catch (error) {
      console.error('Error eliminando token:', error);
    }
  },

  getUser: () => {
    try {
      const user = localStorage.getItem('user');
      return user ? JSON.parse(user) : null;
    } catch (error) {
      console.error('Error parseando usuario:', error);
      return null;
    }
  },

  setUser: (user) => {
    try {
      if (user && user.id && user.nombre) {
        localStorage.setItem('user', JSON.stringify(user));
        console.log('👤 Usuario guardado en localStorage');
      }
    } catch (error) {
      console.error('Error guardando usuario:', error);
    }
  },

  removeUser: () => {
    try {
      localStorage.removeItem('user');
    } catch (error) {
      console.error('Error eliminando usuario:', error);
    }
  },

  clearAll: () => {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      console.log('🧹 LocalStorage limpiado');
    } catch (error) {
      console.error('Error limpiando localStorage:', error);
    }
  }
};

// ============================================================================
// VARIABLE GLOBAL PARA DISPATCH (necesaria para ApiClient)
// ============================================================================
let globalDispatch = null;

// ============================================================================
// CLIENTE API — wrapper fino sobre el apiClient único (src/services/apiClient).
// La lógica de request/token/errores vive ahí; aquí solo se registra qué hacer
// cuando un 401/403 CON token indica sesión caducada (la que antes estaba inline).
// Los consumidores de ApiClient.request/.post no cambian: misma firma, mismo
// envelope de respuesta, mismo comportamiento de sesión.
// ============================================================================
registerUnauthorizedHandler((status, endpoint) => {
  console.log(`🔒 Error ${status} en ${endpoint} - Cerrando sesión automáticamente`);
  TokenStorage.clearAll();
  if (globalDispatch) {
    globalDispatch({ type: AuthActions.SESSION_EXPIRED });
  }
  sessionStorage.setItem('sessionExpired', 'true');
  setTimeout(() => { window.location.reload(); }, 100);
});

const ApiClient = {
  request: (endpoint, options = {}) => apiClient.request(endpoint, options),
  post: (endpoint, data, options = {}) => apiClient.post(endpoint, data, options),
};

// ============================================================================
// CONTEXTO Y HOOK
// ============================================================================
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

// ============================================================================
// PROVIDER DEL CONTEXTO
// ============================================================================
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Guardar dispatch globalmente para ApiClient
  useEffect(() => {
    globalDispatch = dispatch;
    return () => {
      globalDispatch = null;
    };
  }, [dispatch]);

  // Inicialización al cargar la app
  useEffect(() => {
    initializeAuth();
  }, []);

  // ========================================================================
  // LISTENER PARA SESIÓN EXPIRADA
  // ========================================================================
  useEffect(() => {
    if (state.sessionExpired) {
      console.log('⚠️ Sesión expirada detectada, recargando página...');
      
      // Guardar flag en sessionStorage
      sessionStorage.setItem('sessionExpired', 'true');
      
      // Recargar la página
      setTimeout(() => {
        window.location.reload();
      }, 200);
    }
  }, [state.sessionExpired]);
  // ========================================================================

  // ============================================================================
  // FUNCIONES DE VALIDACIÓN
  // ============================================================================
  const validateUserData = (user) => {
    if (!user) {
      console.warn('⚠️ Datos de usuario nulos');
      return false;
    }
    
    if (!user.id || !user.nombre || !user.rol) {
      console.warn('⚠️ Datos de usuario incompletos:', {
        hasId: !!user.id,
        hasNombre: !!user.nombre,
        hasRol: !!user.rol
      });
      return false;
    }
    
    return true;
  };

  // ============================================================================
  // INICIALIZACIÓN DE AUTENTICACIÓN
  // ============================================================================
  const isTokenExpired = (token) => {
    try {
      // JWT firmado: el payload es la segunda parte (base64url). Los tokens
      // legacy (base64 sin firma, pre-migración) no tienen 3 partes y se
      // tratan como caducados: el backend ya no los acepta.
      const parts = token.split('.');
      if (parts.length !== 3) return true;
      const decoded = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      if (!decoded?.exp) return true;
      return decoded.exp < Math.floor(Date.now() / 1000);
    } catch (e) {
      return true;
    }
  };

  const initializeAuth = async () => {
    console.log('🚀 Inicializando AuthContext...');

    try {
      const storedToken = TokenStorage.getToken();
      const storedUser = TokenStorage.getUser();

      console.log('📦 Datos almacenados:', {
        hasToken: !!storedToken,
        hasUser: !!storedUser,
        userName: storedUser?.nombre
      });

      if (storedToken && storedUser && validateUserData(storedUser)) {
        if (isTokenExpired(storedToken)) {
          console.log('⌛ Token caducado, redirigiendo a login');
          TokenStorage.clearAll();
          sessionStorage.setItem('sessionExpired', 'true');
          dispatch({ type: AuthActions.STOP_LOADING });
          return;
        }

        console.log('✅ Restaurando sesión desde localStorage');

        dispatch({
          type: AuthActions.LOGIN_SUCCESS,
          payload: {
            user: storedUser,
            token: storedToken
          }
        });

        // Sincronizar datos frescos del servidor (avatar, rol, etc.) sin bloquear
        // el arranque. Si falla (red/sesión), se ignora: ya hay sesión local.
        getCurrentUser().catch(() => {});
      } else {
        console.log('❌ No hay datos válidos, limpiando');
        TokenStorage.clearAll();
        dispatch({ type: AuthActions.STOP_LOADING });
      }
    } catch (error) {
      console.error('❌ Error inicializando autenticación:', error);
      TokenStorage.clearAll();
      dispatch({ type: AuthActions.STOP_LOADING });
    }
  };

  // ============================================================================
  // FUNCIÓN DE LOGIN
  // ============================================================================
  const login = async (credentials) => {
    console.log('🔐 Intento de login para:', credentials.email);
    dispatch({ type: AuthActions.LOADING });

    try {
      // Login vía apiClient (público: skipAuth, no envía token ni activa el
      // interceptor de sesión). El apiClient lanza HttpError en !ok con el
      // mensaje del servidor y devuelve el envelope completo.
      const data = await apiClient.post('login', {
        email: credentials.email,
        password: credentials.password
      }, { skipAuth: true });
      console.log('📨 Respuesta de login:', data);

      if (data.status !== 'success') {
        throw new Error(data.message || 'Login falló');
      }

      let user = null;
      let sessionToken = null;

      if (data.data && data.data.success && data.data.data) {
        user = data.data.data.user;
        const tokens = data.data.data.tokens;
        
        if (tokens && tokens.access_token) {
          sessionToken = tokens.access_token;
        }
      }

      console.log('📊 Datos extraídos:', {
        user: user,
        hasToken: !!sessionToken
      });

      if (!user) {
        throw new Error('No se recibió información del usuario');
      }

      if (!sessionToken) {
        throw new Error('No se recibió token de autenticación');
      }

      if (!validateUserData(user)) {
        throw new Error('Los datos del usuario están incompletos');
      }

      console.log('✅ Login exitoso para:', user.nombre);

      TokenStorage.setUser(user);
      TokenStorage.setToken(sessionToken);

      dispatch({
        type: AuthActions.LOGIN_SUCCESS,
        payload: { user, token: sessionToken }
      });

      return { success: true };
      
    } catch (error) {
      console.error('❌ Error de login:', error.message);
      
      const errorMessage = error.message || 'Error de conexión';
      
      dispatch({
        type: AuthActions.LOGIN_FAILURE,
        payload: { error: errorMessage }
      });
      
      return { success: false, error: errorMessage };
    }
  };

  // ============================================================================
  // FUNCIÓN DE LOGOUT
  // ============================================================================
  const logout = async () => {
    console.log('🚪 Iniciando logout...');
    
    try {
      if (state.token) {
        try {
          await ApiClient.post('logout');
          console.log('✅ Logout en servidor exitoso');
        } catch (err) {
          console.warn('⚠️ Logout en servidor falló:', err.message);
        }
      }
    } catch (error) {
      console.error('❌ Error en logout del servidor:', error);
    } finally {
      TokenStorage.clearAll();
      dispatch({ type: AuthActions.LOGOUT });
      console.log('✅ Logout completado');
    }
  };

  // ============================================================================
  // FUNCIONES ADICIONALES
  // ============================================================================
  const clearError = () => {
    dispatch({ type: AuthActions.CLEAR_ERROR });
  };

  const getCurrentUser = async () => {
    try {
      const response = await ApiClient.request('me');

      // El endpoint `me` responde { status:'success', data:{ success:true, data:{...usuario} } }
      // (doble anidado, como login). El usuario real está en response.data.data.
      // Se soporta también una forma plana por compatibilidad.
      const user = response?.data?.data || response?.data;

      if (response.status === 'success' && validateUserData(user)) {
        TokenStorage.setUser(user);

        dispatch({
          type: AuthActions.USER_UPDATE,
          payload: { user }
        });

        return user;
      } else {
        throw new Error('Datos de usuario inválidos del servidor');
      }
    } catch (error) {
      console.error('Error obteniendo perfil del usuario:', error);
      throw error;
    }
  };

  const changePassword = async (passwords) => {
    try {
      const response = await ApiClient.post('change-password', passwords);
      return { 
        success: response.status === 'success', 
        message: response.message 
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // ============================================================================
  // UTILIDADES DE PERMISOS
  // ============================================================================
  const hasPermission = (permission) => {
    if (!state.user || !validateUserData(state.user)) return false;

    const rolePermissions = {
      admin: ['create', 'edit', 'delete', 'view', 'manage_users'],
      cliente: ['view']
    };

    return rolePermissions[state.user.rol]?.includes(permission) || false;
  };

  const isAdmin = () => state.user?.rol === 'admin';
  // Usuario interno de agencia (admin o manager): mismo criterio que los gates
  // de módulo del backend (changelog, crea_*).
  const isManager = () => state.user?.rol === 'manager';
  const canCreateTasks = () => hasPermission('create');
  const canEditTasks = () => hasPermission('edit');
  const canDeleteTasks = () => hasPermission('delete');

  // ============================================================================
  // VALOR DEL CONTEXTO
  // ============================================================================
  const value = {
    // Estado
    user: state.user,
    token: state.token,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    error: state.error,
    sessionExpired: state.sessionExpired,
    
    // Funciones principales
    login,
    logout,
    getCurrentUser,
    changePassword,
    clearError,
    
    // Utilidades de permisos
    hasPermission,
    isAdmin,
    isManager,
    canCreateTasks,
    canEditTasks,
    canDeleteTasks,
    
    // API Client
    ApiClient
  };

  // ============================================================================
  // DEBUG LOGGING
  // ============================================================================
  if (process.env.NODE_ENV === 'development') {
    console.log('🔍 AuthContext State:', {
      isAuthenticated: state.isAuthenticated,
      isLoading: state.isLoading,
      hasUser: !!state.user,
      hasToken: !!state.token,
      userName: state.user?.nombre,
      userRole: state.user?.rol,
      error: state.error,
      sessionExpired: state.sessionExpired
    });
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// ============================================================================
// EXPORTACIONES
// ============================================================================
export { ApiClient };

// ============================================================================
// FUNCIÓN DE DEBUG GLOBAL (solo en desarrollo: en producción exponía el token
// completo y el localStorage a cualquiera con la consola abierta)
// ============================================================================
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.debugAuth = () => {
    const storedUser = TokenStorage.getUser();
    const storedToken = TokenStorage.getToken();

    return {
      stored: {
        user: storedUser,
        tokenStart: storedToken?.substring(0, 30) + '...',
        isUserValid: storedUser ? (storedUser.id && storedUser.nombre && storedUser.rol) : false
      },
      taskStoreConnected: !!taskStoreNotifier,
      actions: {
        clearAuth: () => TokenStorage.clearAll(),
        testNotification: () => notifyTaskStore('TEST', { message: 'Test de notificación' })
      }
    };
  };

  console.log('🛠️ AuthContext debug disponible: window.debugAuth()');
}